import React, { useState } from 'react';
import Modal from '../common/Modal';
import FormField from '../common/FormField';
import TextAreaField from '../common/TextAreaField';
import { superAdminApi } from '../../services/superAdminApi';

const ApprovalModal = ({ request, onClose, onComplete }) => {
  const [formData, setFormData] = useState({
    admin_notes: '',
    subdomain: request.organization_name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'),
    domain: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      await superAdminApi.approveSignupRequest(request.id, formData);
      onComplete();
    } catch (error) {
      if (error.response?.status === 422) {
        setErrors(error.response.data.errors || {});
      } else {
        setErrors({ 
          general: error.response?.data?.message || 'Failed to approve request' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  return (
    <Modal onClose={onClose} title="Approve Signup Request" size="large">
      <div className="approval-modal">
        <div className="request-summary">
          <h3>Request Details</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <label>Organization:</label>
              <span>{request.organization_name}</span>
            </div>
            <div className="summary-item">
              <label>Contact Person:</label>
              <span>{request.contact_person}</span>
            </div>
            <div className="summary-item">
              <label>Email:</label>
              <span>{request.email}</span>
            </div>
            {request.phone && (
              <div className="summary-item">
                <label>Phone:</label>
                <span>{request.phone}</span>
              </div>
            )}
            {request.website && (
              <div className="summary-item">
                <label>Website:</label>
                <span>
                  <a href={request.website} target="_blank" rel="noopener noreferrer">
                    {request.website}
                  </a>
                </span>
              </div>
            )}
          </div>
          
          {request.description && (
            <div className="description-section">
              <label>Description:</label>
              <div className="description-content">{request.description}</div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="approval-form">
          <h3>Tenant Configuration</h3>
          
          {errors.general && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.general}
            </div>
          )}

          <div className="form-grid">
            <FormField
              label="Subdomain *"
              type="text"
              value={formData.subdomain}
              onChange={(value) => handleChange('subdomain', value)}
              placeholder="organization-name"
              error={errors.subdomain?.[0]}
              required
              fullWidth
            />

            <FormField
              label="Custom Domain (Optional)"
              type="text"
              value={formData.domain}
              onChange={(value) => handleChange('domain', value)}
              placeholder="example.com"
              error={errors.domain?.[0]}
              fullWidth
            />
          </div>

          <TextAreaField
            label="Admin Notes (Optional)"
            value={formData.admin_notes}
            onChange={(value) => handleChange('admin_notes', value)}
            placeholder="Add any notes about this approval..."
            rows={3}
            error={errors.admin_notes?.[0]}
            fullWidth
          />

          <div className="preview-section">
            <h4>Generated URLs:</h4>
            <div className="url-preview">
              <div className="url-item">
                <label>Public Website:</label>
                <span className="url">
                  {formData.domain ? 
                    `https://${formData.domain}` : 
                    `https://${formData.subdomain}.abfisaas.com`
                  }
                </span>
              </div>
              <div className="url-item">
                <label>Admin Portal:</label>
                <span className="url">
                  {formData.domain ? 
                    `https://${formData.domain}/admin` : 
                    `https://${formData.subdomain}.abfisaas.com/admin`
                  }
                </span>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              onClick={onClose} 
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading} 
              className="btn-success"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Approving...
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i>
                  Approve Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ApprovalModal;